import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScanLine, Camera } from "lucide-react";

// Optional Phase 3 verification: USB/Bluetooth scanners type the code + Enter into the
// input; the camera is a convenience. Either way the result is only *looked up* —
// goods are still released through the manual Confirm Pickup flow.
export default function PickupScanner({ open, onClose, onScan }) {
  const [code, setCode] = useState("");
  const [camOn, setCamOn] = useState(false);
  const [camErr, setCamErr] = useState("");
  const qrRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
    else { setCode(""); setCamErr(""); setCamOn(false); }
  }, [open]);

  useEffect(() => {
    if (!camOn) return;
    let stopped = false;
    const inst = new Html5Qrcode("pickup-cam");
    qrRef.current = inst;
    inst.start({ facingMode: "environment" }, { fps: 8, qrbox: 220 }, (text) => {
      if (stopped) return;
      stopped = true;
      inst.stop().catch(() => {});
      setCamOn(false);
      onScan(text);
    }, () => {}).catch((e) => { setCamErr("Camera unavailable. Type or scan the code below."); setCamOn(false); });
    return () => { stopped = true; inst.stop().catch(() => {}); };
  }, [camOn, onScan]);

  const submit = (e) => { e.preventDefault(); if (code.trim()) { onScan(code.trim()); setCode(""); } };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pickup-scanner-modal">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><ScanLine className="w-5 h-5" /> Scan Pickup Code</DialogTitle>
          <DialogDescription>Scan the QR on the customer's receipt. This opens the order. You still confirm the quantity handed over.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex gap-2">
          <Input ref={inputRef} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan or type code / PU-000123" className="mono" data-testid="scan-code-input" autoComplete="off" />
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="scan-submit-btn">Open</Button>
        </form>
        {camOn ? <div id="pickup-cam" className="rounded-lg overflow-hidden" data-testid="pickup-cam" /> : (
          <Button variant="outline" onClick={() => { setCamErr(""); setCamOn(true); }} data-testid="scan-camera-btn"><Camera className="w-4 h-4 mr-1.5" /> Use camera</Button>
        )}
        {camErr && <p className="text-xs text-amber-600" data-testid="scan-cam-error">{camErr}</p>}
      </DialogContent>
    </Dialog>
  );
}
